import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge, Button, Card, EmptyState, Table, Textarea, confirmDialog, type TableColumn } from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import { exchangeApi, apiErrorMessage, type Commissions, type KycDocument, type OwnerType } from "../exchange/api";
import { PAYMENT_LABELS, fmtDateTime, fmtDay, money, pct, relative } from "../exchange/format";
import { ErrorState, Kpi, Loader, Section } from "../exchange/components";

// ===========================================================================
// Cargo Market — Administración de la plataforma (Coding Cloud / WAYAK).
// El backend exige requirePlatformAdmin; un 403 se muestra como «sólo admins».
// ===========================================================================

const OWNER_LABELS: Record<OwnerType, string> = {
  company: "Empresa",
  vehicle: "Unidad",
  trailer: "Remolque",
  driver: "Operador",
  shipper: "Embarcador",
};

/** Carga con detección de 403 (el endpoint es sólo para admins de la plataforma). */
function useAdminLoad<T>(fn: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      setForbidden(false);
      setData(await fn());
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) setForbidden(true);
      else setError(apiErrorMessage(err, "No se pudo cargar la información"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, setData, loading, error, forbidden, reload };
}

function Forbidden() {
  return (
    <EmptyState
      icon="ri-lock-line"
      title="Sólo administradores de la plataforma"
      description="Esta sección es para el equipo de WAYAK que revisa expedientes y concilia comisiones."
    />
  );
}

// ===========================================================================
// 6. Cola de verificaciones (KYC)
// ===========================================================================

function RejectModal({ doc, onClose, onDone }: { doc: KycDocument; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const submit = async () => {
    setTouched(true);
    if (!note.trim()) return;
    setSaving(true);
    try {
      await exchangeApi.adminReview(doc.id, "rejected", note.trim());
      toast.success(`Rechazado: ${doc.label}`);
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      title="Rechazar documento"
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <div className="rounded-lg border border-line bg-app p-3 text-sm">
            <p className="font-medium text-content">{doc.label}</p>
            <p className="text-xs text-content-muted mt-0.5">{doc.companyName || doc.company_id} · {OWNER_LABELS[doc.ownerType]}</p>
          </div>
          <Textarea
            label={<>Motivo (lo verá la empresa)<span className="text-error"> *</span></>}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ej. El documento está vencido o ilegible; sube la versión vigente."
            error={touched && !note.trim() ? "Indica el motivo para que puedan corregirlo" : undefined}
          />
          <div className="flex gap-3 border-t border-line pt-4">
            <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button variant="danger" shape="pill" className="flex-1" loading={saving} onClick={submit}>Rechazar</Button>
          </div>
        </div>
      }
    />
  );
}

export function VerificacionesAdminSection() {
  const q = useAdminLoad(() => exchangeApi.adminDocuments());
  const [rejecting, setRejecting] = useState<KycDocument | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const remove = (id: string) => q.setData((prev) => (prev ? prev.filter((d) => d.id !== id) : prev));

  const approve = async (doc: KycDocument) => {
    const ok = await confirmDialog({ title: "¿Aprobar documento?", text: `${doc.label} · ${doc.companyName || doc.company_id}`, confirmText: "Aprobar", tone: "success" });
    if (!ok) return;
    setBusyId(doc.id);
    try {
      await exchangeApi.adminReview(doc.id, "approved");
      toast.success(`Aprobado: ${doc.label}`);
      remove(doc.id);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const columns: TableColumn<KycDocument>[] = [
    {
      key: "company",
      header: "Empresa",
      render: (d) => (
        <span className="inline-flex flex-col">
          <span className="font-medium">{d.companyName || d.company_id}</span>
          {d.sample && <Badge size="sm" tone="warning" className="mt-0.5 w-fit">Ejemplo (Sandbox)</Badge>}
        </span>
      ),
    },
    {
      key: "owner",
      header: "Dueño",
      render: (d) => (
        <span className="inline-flex flex-col">
          <Badge size="sm" tone={d.ownerType === "company" || d.ownerType === "shipper" ? "primary" : "neutral"}>{OWNER_LABELS[d.ownerType] ?? d.ownerType}</Badge>
          {d.ownerType !== "company" && d.ownerType !== "shipper" && <span className="text-[11px] text-content-faint mt-0.5">{d.ownerName || `ID ${d.ownerId}`}</span>}
        </span>
      ),
    },
    { key: "label", header: "Documento", render: (d) => d.label },
    { key: "number", header: "Número", render: (d) => d.number || "—", hideOnMobile: true },
    {
      key: "dates",
      header: "Emisión / vence",
      render: (d) => `${d.issuedAt ? fmtDay(d.issuedAt) : "—"} / ${d.expiresAt ? fmtDay(d.expiresAt) : "—"}`,
    },
    {
      key: "file",
      header: "Archivo",
      render: (d) =>
        d.fileUrl ? (
          <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline inline-flex items-center gap-1">
            <i className="ri-file-pdf-2-line" />Abrir
          </a>
        ) : (
          <span className="text-content-faint">Sin archivo</span>
        ),
    },
    { key: "sent", header: "Enviado", render: (d) => <span title={fmtDateTime(d.createdAt)}>{relative(d.createdAt) || "—"}</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (d) => (
        <span className="inline-flex gap-2">
          <Button size="sm" variant="successSoft" loading={busyId === d.id} disabled={!!busyId} icon={<i className="ri-check-line" />} onClick={() => void approve(d)}>Aprobar</Button>
          <Button size="sm" variant="dangerSoft" disabled={!!busyId} icon={<i className="ri-close-line" />} onClick={() => setRejecting(d)}>Rechazar</Button>
        </span>
      ),
    },
  ];

  return (
    <Section
      title="Verificaciones"
      subtitle="Expedientes de KYC por revisar. Al aprobar o rechazar se recalcula el nivel de la empresa y se le avisa."
      actions={!q.forbidden ? <Button size="sm" variant="outline" icon={<i className="ri-refresh-line" />} onClick={() => void q.reload()}>Actualizar</Button> : undefined}
    >
      {q.forbidden ? (
        <Forbidden />
      ) : q.loading && !q.data ? (
        <Loader />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={q.reload} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState icon="ri-shield-check-line" title="No hay documentos por revisar" description="Cuando una empresa suba un documento aparecerá aquí." />
      ) : (
        <>
          <p className="text-sm text-content-muted mb-3">
            {(q.data ?? []).length} documento(s) pendientes, los más antiguos primero.
            {(q.data ?? []).some((d) => d.sample) && " Los marcados «Ejemplo (Sandbox)» son de la compañía activa y se retiran al apagar su Sandbox."}
          </p>
          <Table columns={columns} rows={q.data ?? []} rowKey={(d) => d.id} />
        </>
      )}
      {rejecting && (
        <RejectModal
          doc={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            remove(rejecting.id);
            setRejecting(null);
          }}
        />
      )}
    </Section>
  );
}

// ===========================================================================
// 7. Comisiones
// ===========================================================================

type CommissionRow = Commissions["rows"][number];

/** Viajes cerrados de ejemplo del Sandbox de la compañía activa: aparte, nunca en los totales reales. */
function SampleCommissions({ sample, columns }: { sample: NonNullable<Commissions["sample"]>; columns: TableColumn<CommissionRow>[] }) {
  return (
    <Card padding="sm" surface="app">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge tone="warning">Ejemplo (Sandbox)</Badge>
        <span className="text-xs text-content-muted">
          {sample.totals.trips} viaje(s) cerrados de ejemplo de la compañía activa · comisión {money(sample.totals.commission)}. No suman a los totales reales.
        </span>
      </div>
      <Table columns={columns} rows={sample.rows} rowKey={(r) => r.id} emptyText="Sin viajes cerrados de ejemplo." />
    </Card>
  );
}

export function ComisionesAdminSection() {
  const q = useAdminLoad(() => exchangeApi.adminCommissions());

  const columns: TableColumn<CommissionRow>[] = [
    { key: "folio", header: "Folio", render: (r) => <span className="font-medium">{r.folio}</span> },
    { key: "shipper", header: "Embarcador", render: (r) => r.shipperName },
    { key: "carrier", header: "Transportista", render: (r) => r.carrierName },
    { key: "closed", header: "Cerrado", render: (r) => fmtDay(r.closedAt) },
    { key: "freight", header: "Flete", align: "right", render: (r) => money(r.freight) },
    { key: "commission", header: "Comisión", align: "right", render: (r) => <span className="font-semibold">{money(r.commission)}</span> },
    { key: "total", header: "Total", align: "right", render: (r) => money(r.total) },
    {
      key: "payment",
      header: "Pago",
      render: (r) => {
        const p = r.payment ? PAYMENT_LABELS[r.payment] : null;
        return p ? <Badge tone={p.tone}>{p.label}</Badge> : <Badge tone="neutral">{r.payment || "—"}</Badge>;
      },
    },
  ];

  return (
    <Section
      title="Comisiones"
      subtitle="Viajes cerrados en el mercado (sin datos de Sandbox) y lo que le corresponde a WAYAK."
      actions={!q.forbidden ? <Button size="sm" variant="outline" icon={<i className="ri-refresh-line" />} onClick={() => void q.reload()}>Actualizar</Button> : undefined}
    >
      {q.forbidden ? (
        <Forbidden />
      ) : q.loading && !q.data ? (
        <Loader />
      ) : q.error || !q.data ? (
        <ErrorState error={q.error || "Sin datos"} onRetry={q.reload} />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi label="Viajes cerrados" value={q.data.totals.trips} icon="ri-flag-2-line" />
            <Kpi label="Flete total" value={money(q.data.totals.freight)} icon="ri-truck-line" tone="info" hint="Pagado a transportistas" />
            <Kpi label="Comisión total" value={money(q.data.totals.commission)} icon="ri-money-dollar-circle-line" tone="success" hint="Ingreso de WAYAK" />
            <Kpi label="% de comisión" value={pct(q.data.commissionPct)} icon="ri-percent-line" tone="warning" hint="Sobre el flete" />
          </div>
          <Card padding="sm" surface="app">
            <p className="text-xs text-content-muted">
              <i className="ri-information-line mr-1 text-info" />
              WAYAK factura sólo su comisión; el flete se paga directo al transportista (pagos divididos con Stripe Connect — pendiente de configurar).
            </p>
          </Card>
          <Table columns={columns} rows={q.data.rows} rowKey={(r) => r.id} emptyText="Aún no hay viajes cerrados." />
          {q.data.sample && <SampleCommissions sample={q.data.sample} columns={columns} />}
        </div>
      )}
    </Section>
  );
}
